import { useState, useMemo, useEffect } from 'react';
import { Button } from './ui/button';
import { Combobox, type ComboboxOption } from './ui/combobox';
import { Label } from './ui/label';
import { Check } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import type { ReportBuilderDimension } from '../services/reportBuilder.service';

interface DimensionFilterOption {
  dimensionId: string;
  dimensionName: string;
  value: any;
}

interface DrilldownOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isDimension: boolean;
  // For dimension filter
  dimensionName?: string;
  dimensionValue?: any;
  // For metric drilldown
  metricName?: string;
  availableDimensions?: ReportBuilderDimension[];
  currentDimensionIds?: string[];
  rowDimensions?: DimensionFilterOption[];
  onDimensionFilter: () => void;
  onDimensionFilterNoRefresh?: () => void;
  onMetricDrilldown: (dimensionId: string, selectedFilterDimensionIds: string[]) => void;
}

export function DrilldownOverlay({
  open,
  onOpenChange,
  isDimension,
  dimensionName,
  dimensionValue,
  metricName,
  availableDimensions = [],
  currentDimensionIds = [],
  rowDimensions = [],
  onDimensionFilter,
  onDimensionFilterNoRefresh,
  onMetricDrilldown,
}: DrilldownOverlayProps) {
  const [selectedDimensionId, setSelectedDimensionId] = useState<string>('');
  const [selectedFilterDimensionIds, setSelectedFilterDimensionIds] = useState<Set<string>>(new Set());

  // Filter out dimensions that are already in the current analysis
  const drilldownOptions = useMemo(() => {
    return availableDimensions
      .filter(dim => !currentDimensionIds.includes(dim.id))
      .map(dim => ({
        value: dim.id,
        label: dim.name,
      }));
  }, [availableDimensions, currentDimensionIds]);

  // Initialize selected filter dimensions to all dimensions by default
  useEffect(() => {
    if (open && rowDimensions && rowDimensions.length > 0) {
      setSelectedFilterDimensionIds(new Set(rowDimensions.map(d => d.dimensionId)));
    }
  }, [open, rowDimensions]);

  // Reset state when overlay closes
  useEffect(() => {
    if (!open) {
      setSelectedDimensionId('');
      setSelectedFilterDimensionIds(new Set());
    }
  }, [open]);

  const handleToggleFilterDimension = (dimensionId: string) => {
    setSelectedFilterDimensionIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dimensionId)) {
        newSet.delete(dimensionId);
      } else {
        newSet.add(dimensionId);
      }
      return newSet;
    });
  };

  const handleDimensionFilter = () => {
    onDimensionFilter();
    onOpenChange(false);
  };

  const handleDimensionFilterNoRefresh = () => {
    if (onDimensionFilterNoRefresh) {
      onDimensionFilterNoRefresh();
      onOpenChange(false);
    }
  };

  const handleMetricDrilldown = () => {
    if (selectedDimensionId) {
      onMetricDrilldown(selectedDimensionId, Array.from(selectedFilterDimensionIds));
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isDimension ? 'Filter by Dimension' : 'Drill Down'}</DialogTitle>
          <DialogDescription>
            {isDimension ? (
              <>Do you want to filter where <strong>{dimensionName}</strong> = <strong>{String(dimensionValue)}</strong>?</>
            ) : (
              <>Pick another dimension to see how this <strong>{metricName}</strong> number breaks down.</>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <DialogBody className="flex-1 overflow-y-auto">
          {!isDimension && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Select dimension to drill down by:</Label>
                <Combobox
                  options={drilldownOptions}
                  value={selectedDimensionId}
                  onValueChange={setSelectedDimensionId}
                  placeholder="Select a dimension..."
                  emptyText="No available dimensions"
                />
              </div>
              
              {rowDimensions && rowDimensions.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">
                    Filter by these dimensions from this row (uncheck to exclude):
                  </Label>
                  <div className="space-y-2 rounded-md border p-3">
                    {rowDimensions.map((dim) => {
                      const isChecked = selectedFilterDimensionIds.has(dim.dimensionId);
                      return (
                        <label
                          key={dim.dimensionId}
                          className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-2 rounded transition-colors"
                        >
                          <div className="relative flex h-4 w-4 items-center justify-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggleFilterDimension(dim.dimensionId)}
                              className={`h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                                isChecked 
                                  ? 'bg-primary text-primary-foreground' 
                                  : 'bg-background'
                              }`}
                            />
                            {isChecked && (
                              <Check className="absolute h-3 w-3 text-primary-foreground pointer-events-none" />
                            )}
                          </div>
                          <span className="text-sm">
                            <strong>{dim.dimensionName}</strong> = {String(dim.value)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {isDimension ? (
            <>
              {onDimensionFilterNoRefresh && (
                <Button variant="secondary" onClick={handleDimensionFilterNoRefresh}>
                  Add Filter Only
                </Button>
              )}
              <Button onClick={handleDimensionFilter}>
                Add Filter & Refresh
              </Button>
            </>
          ) : (
            <Button onClick={handleMetricDrilldown} disabled={!selectedDimensionId}>
              Drill Down
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

